import { go } from '../../App';
import { usePolled } from '../../data';
import { InkHero, Loading, MoneyText, Panel, StatusChip, ago, formatWhen } from '../../ui';
import { Booking } from '../../types';

interface DirectoryItem {
  kind: 'connection' | 'invitation';
  name: string;
  status: string;
  supplierAccountId?: string;
  // N19 — how much of this supplier is actually in the catalogue. A connected,
  // Active supplier with `unlisted > 0` cannot be booked for those trips, and
  // only the hotel can fix it (DECISION-016: the supplier owns the price, the
  // hotel owns the margin).
  products?: { exposed: number; total: number; unlisted: number };
}

// The hotel home. Overview template: the hero owns the screen's most
// important number — how many requests are awaiting an answer, and how long
// the oldest has waited (flow H2). The panel below is the waiting list
// itself; a row opens the booking in the register's drawer.
export function Dashboard() {
  const { data, error } = usePolled<{ items: Booking[] }>('/bookings');
  const directory = usePolled<{ items: DirectoryItem[] }>('/suppliers');
  const items = data ? data.items : null;
  // "This is not me" NOTIFIES the hotel (canon 11) — it lands on the home
  // screen, not just as a row they would have to go looking for.
  const burned = (directory.data?.items ?? []).filter((s) => s.status === 'burned');
  const unlisted = (directory.data?.items ?? []).filter(
    (s) => s.kind === 'connection' && s.status === 'active' && (s.products?.unlisted ?? 0) > 0,
  );

  const waiting = (items ?? [])
    .filter((b) => b.status === 'pending' || b.status === 'requested')
    .sort((a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt));
  const alternatives = (items ?? []).filter((b) => b.status === 'alternative');
  const declined = (items ?? []).filter(
    (b) => b.status === 'declined' && Date.parse(b.start) > Date.now(),
  );
  // Expired unanswered (the system closed it at the deadline) — V2: the
  // hotel is told plainly, in time to reroute.
  const expired = (items ?? []).filter(
    (b) => b.status === 'cancelled' && b.cancelReason === 'no_answer' && Date.parse(b.start) > Date.now(),
  );
  const confirmed = (items ?? []).filter((b) => b.status === 'confirmed');
  const cameBack = alternatives.length + declined.length + expired.length;
  const oldest = waiting[0];
  // The review prompt (DECISION-056): completed jobs the hotel hasn't rated yet.
  // Computed from the bookings it already polls — completion invites the review
  // (canon 10 state 6), and each clears the moment it is rated.
  const toReview = (items ?? []).filter((b) => b.status === 'completed' && !b.review);

  return (
    <div>
      <InkHero
        eyebrow="Awaiting an answer"
        figure={items === null ? '…' : waiting.length}
        unit={waiting.length === 1 ? 'request' : 'requests'}
        explain={
          oldest
            ? `The oldest has waited ${ago(oldest.createdAt)}.`
            : items === null
              ? ' '
              : 'Nothing is waiting on a supplier right now.'
        }
      />

      {burned.length > 0 && (
        <Panel
          style={{ background: 'var(--color-warning-light)', borderColor: 'var(--color-warning-border)', marginBottom: 20, cursor: 'pointer' }}
          onClick={() => go('/suppliers')}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span className="uchip escalated">Wrong hands</span>
            <span style={{ color: 'var(--text-primary)' }}>
              {burned.length === 1
                ? `Your invitation to ${burned[0]!.name} reached the wrong person — check the address and re-send.`
                : `${burned.length} invitations reached the wrong person — check the addresses and re-send.`}
            </span>
          </div>
        </Panel>
      )}

      {/* N19 — the silent gap. A supplier finishes setting up, goes Active, and
          is STILL unbookable until this hotel puts their trips in its catalogue
          with a margin. That is correct (DECISION-016) and it was nobody's
          prompt: the supplier's phone said "you're all caught up", this screen
          said nothing, and the booking that never came had no explanation on
          either side. Found on a real tenant, 2026-08-24. */}
      {unlisted.length > 0 && (
        <Panel
          style={{ background: 'var(--color-warning-light)', borderColor: 'var(--color-warning-border)', marginBottom: 20, cursor: 'pointer' }}
          onClick={() => go('/products')}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span className="uchip soon">Not bookable yet</span>
            <span style={{ color: 'var(--text-primary)' }}>
              {unlisted.length === 1
                ? `${unlisted[0]!.name} has ${unlisted[0]!.products!.unlisted} trip${unlisted[0]!.products!.unlisted === 1 ? '' : 's'} you have not put in your catalogue — set a margin and you can book ${unlisted[0]!.products!.unlisted === 1 ? 'it' : 'them'}.`
                : `${unlisted.length} connected suppliers have trips that are not in your catalogue — set a margin on each to make them bookable.`}
            </span>
          </div>
        </Panel>
      )}

      {cameBack > 0 && (
        <Panel
          // The notice wears the leading status's own surface: saffron for a
          // counter-offer, the status map's rust for declines — one status,
          // one colour, everywhere.
          style={{
            background:
              alternatives.length > 0
                ? 'var(--color-primary-light)'
                : declined.length > 0
                  ? 'var(--color-error-light)'
                  : 'var(--color-warning-light)',
            borderColor:
              alternatives.length > 0
                ? 'var(--color-primary-border)'
                : declined.length > 0
                  ? 'var(--color-error-border)'
                  : 'var(--color-warning-border)',
            marginBottom: 20,
            cursor: 'pointer',
          }}
          onClick={() =>
            go(
              cameBack === 1 && alternatives.length === 1
                ? `/alternatives/${alternatives[0]!.id}`
                : '/alternatives',
            )
          }
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {alternatives.length > 0 || declined.length > 0 ? (
              <StatusChip status={alternatives.length > 0 ? 'alternative' : 'declined'} />
            ) : (
              <span className="uchip escalated">No answer</span>
            )}
            <span style={{ color: 'var(--text-primary)' }}>
              {alternatives.length === 1 && cameBack === 1
                ? `${alternatives[0]!.supplierName} offered a different time for ${alternatives[0]!.productName} — it needs your answer.`
                : declined.length === cameBack && declined.length > 0
                  ? declined.length === 1
                    ? `${declined[0]!.supplierName} declined ${declined[0]!.productName} — the guest needs another plan.`
                    : `${declined.length} requests were declined — the guests need another plan.`
                  : expired.length === cameBack
                    ? expired.length === 1
                      ? `${expired[0]!.supplierName} didn’t answer ${expired[0]!.productName} — it expired, the guest needs another plan.`
                      : `${expired.length} requests expired unanswered — the guests need another plan.`
                    : `${cameBack} requests came back — declined, expired or with a different time — they need your answer.`}
            </span>
          </div>
        </Panel>
      )}

      {toReview.length > 0 && (
        <Panel
          style={{ background: 'var(--color-primary-light)', borderColor: 'var(--color-primary-border)', marginBottom: 20, cursor: 'pointer' }}
          onClick={() => go(toReview.length === 1 ? `/bookings/${toReview[0]!.id}` : '/bookings?f=completed')}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span className="uchip soon">Rate supplier</span>
            <span style={{ color: 'var(--text-primary)' }}>
              {toReview.length === 1
                ? `${toReview[0]!.productName} with ${toReview[0]!.supplierName} is done — how did it go? Leave a review.`
                : `${toReview.length} completed jobs are waiting for your review.`}
            </span>
          </div>
        </Panel>
      )}

      {items === null ? (
        <Panel title="Waiting on a supplier" meta={`${confirmed.length} confirmed on the book`}>
          {error ? <p className="error-line">{error}</p> : <Loading />}
        </Panel>
      ) : waiting.length === 0 ? (
        <Panel title="Waiting on a supplier" meta={`${confirmed.length} confirmed on the book`}>
          <div className="empty-neutral">Every request has an answer.</div>
        </Panel>
      ) : (
        <Panel title="Waiting on a supplier" meta={`${confirmed.length} confirmed on the book`} flush>
          <table>
            <tbody>
              {waiting.map((b) => (
                <tr key={b.id} className="click" onClick={() => go(`/bookings/${b.id}`)}>
                  <td className="mono">{formatWhen(b.start)}</td>
                  <td>
                    {b.productName}
                    <br />
                    <span className="hint">
                      {b.supplierName} · {b.guestName}
                    </span>
                  </td>
                  <td>
                    {/* H2: the hotel can see how hard the supplier has been
                        chased — the ladder's state, not just the wait. */}
                    {b.chase?.state === 'escalated' ? (
                      <span className="uchip escalated">Escalated · {ago(b.createdAt)}</span>
                    ) : b.chase?.state === 'surfaced' ? (
                      <span className="uchip soon">Being chased · {ago(b.createdAt)}</span>
                    ) : (
                      <span className="uchip waiting">Waiting {ago(b.createdAt)}</span>
                    )}
                  </td>
                  <td className="num">
                    <MoneyText money={b.guestAmount} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      )}
    </div>
  );
}
